import {
  Column,
  Table as ITable,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { CSSProperties, useMemo } from 'react'

import { cn } from '../utils'
import { Icon } from './icon'
import { Skeleton } from './skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './table'

interface DataTableProps {
  columns: any[]
  data: any[]
  className?: string
  sorting?: SortingState
  rowSelection?: Record<string, boolean>
  getRowId?: (row: any) => string
  onSortChange?: (sort: SortingState) => void
  enableRowSelection?: boolean
  onRowSelectionChange?: (selection: Record<string, boolean>) => void
  stickyHeader?: boolean
  enableColumnResizing?: boolean
  loading?: boolean
  manualSorting?: boolean
  showHeader?: boolean
}

const LOADING_PLACEHOLDER = [{}, {}, {}]

// JANK!
// Use this sin to make a column that should fill out the available space in your table
export const AUTO_WIDTH = 'auto' as unknown as number

export function DataTable({
  columns,
  data,
  className,
  sorting,
  rowSelection,
  onSortChange,
  enableRowSelection,
  onRowSelectionChange,
  stickyHeader,
  loading,
  getRowId,
  enableColumnResizing,
  manualSorting,
  showHeader = true,
}: DataTableProps) {
  const table = useReactTable({
    columnResizeMode: 'onChange',
    columns,
    data: loading ? LOADING_PLACEHOLDER : data,
    defaultColumn: {
      minSize: 60,
      maxSize: 800,
    },
    initialState: {
      columnPinning: {
        left: [],
        right: ['sticky'],
      },
    },
    getRowId,
    enableColumnResizing: !!enableColumnResizing,
    manualSorting: manualSorting,
    enableRowSelection: enableRowSelection,
    enableMultiRowSelection: enableRowSelection,
    onRowSelectionChange: onRowSelectionChange
      ? (mutator) => {
          if (typeof mutator === 'function' && rowSelection) {
            onRowSelectionChange(mutator(rowSelection))
          } else {
            onRowSelectionChange(mutator as Record<string, boolean>)
          }
        }
      : undefined,
    onSortingChange: onSortChange
      ? (updaterOrValue) => {
          if (!sorting) {
            console.warn('no sorting state provided')
          } else {
            if (typeof updaterOrValue === 'function') {
              onSortChange(updaterOrValue(sorting))
            } else {
              onSortChange(updaterOrValue)
            }
          }
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting: sorting, rowSelection: rowSelection ?? {} },
  })

  const hasFooter = useMemo(() => columns.some((col) => col?.footer), [columns])

  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders()
    const colSizes: Record<string, string> = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!
      const size = header.column.columnDef.size as string | number | undefined
      colSizes[`--header-${header.id}-size`] =
        size === 'auto' ? 'auto' : `calc(${header.getSize()}px)`
      colSizes[`--col-${header.column.id}-size`] =
        size === 'auto' ? 'auto' : `calc(${header.column.getSize()}px)`
    }
    return colSizes
  }, [table.getState().columnSizingInfo])

  return (
    <div className={cn('rounded-md border', className)}>
      <Table
        className="min-w-full"
        style={{
          ...columnSizeVars, //Define column sizes on the <table> element
          width: table.getTotalSize(),
        }}
      >
        {showHeader && (
          <TableHeader
            className={cn({ 'bg-background sticky -top-px left-0 z-10': stickyHeader })}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="w-fit">
                {headerGroup.headers.map((header) => {
                  const isSorted = !!header.column.getIsSorted()
                  const isSortable =
                    header.column.columnDef.enableSorting && !!onSortChange
                  const alignment =
                    (
                      header.column.columnDef?.meta as {
                        align?: 'left' | 'right' | 'center'
                      }
                    )?.align || 'left'
                  return (
                    <TableHead
                      key={header.id}
                      className={cn('group relative last:border-r-0', {
                        'active:bg-accent': isSortable,
                      })}
                      colSpan={header.colSpan}
                      style={{
                        width: `var(--header-${header?.id}-size)`,
                        textAlign: alignment,
                      }}
                    >
                      <div
                        className={cn('group flex items-center truncate', {
                          'justify-end': alignment === 'right',
                        })}
                      >
                        <span className="truncate">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </span>
                        {isSortable && (
                          <Icon
                            name="arrow-down"
                            onClick={header.column.getToggleSortingHandler()}
                            className={cn(
                              'cursor-pointer transition-transform group-hover:scale-100',
                              {
                                'scale-50 opacity-0 group-hover:opacity-25': !isSorted,
                                'rotate-180': header.column.getIsSorted() !== 'asc',
                              },
                            )}
                          />
                        )}
                      </div>
                      {enableColumnResizing && (
                        <div
                          {...{
                            onDoubleClick: () => header.column.resetSize(),
                            onMouseDown: header.getResizeHandler(),
                            onTouchStart: header.getResizeHandler(),

                            className: `bg-accent active:bg-primary active:opacity-100 opacity-0 group-hover:opacity-100 h-full cursor-ew-resize touch-none select-none w-2 absolute top-0 right-0 ${
                              header.column.getIsResizing() ? 'isResizing' : ''
                            }`,
                          }}
                        />
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        {hasFooter && (
          <TableFooter>
            {table.getFooterGroups().map((footerGroup, index) =>
              index === 0 ? (
                <TableRow key={footerGroup.id} className="w-fit">
                  {footerGroup.headers.map((header) => {
                    const alignment =
                      (
                        header.column.columnDef?.meta as {
                          align?: 'left' | 'right' | 'center'
                        }
                      )?.align || 'left'
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          'border-b-[3px] border-t-[3px] font-semibold last:border-r-0',
                        )}
                        colSpan={header.colSpan}
                        style={{
                          width: `var(--header-${header?.id}-size)`,
                          textAlign: alignment,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext(),
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ) : null,
            )}
          </TableFooter>
        )}
        <TableBodyRender table={table} colspan={columns.length} loading={loading} />
      </Table>
    </div>
  )
}

//un-memoized normal table body component - see memoized version below
function TableBodyRender({
  table,
  colspan,
  loading,
}: {
  table: ITable<unknown>
  colspan: number
  loading?: boolean
}) {
  return (
    <TableBody>
      {table.getRowModel().rows?.length ? (
        table.getRowModel().rows.map((row) => (
          <TableRow
            className="group/row"
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                size="compact"
                // className="text-base whitespace-nowrap truncate hover:text-clip"
                style={{
                  ...getCommonPinningStyles(cell.column),
                  textAlign:
                    (
                      cell.column.columnDef?.meta as {
                        align?: 'left' | 'right' | 'center'
                      }
                    )?.align || 'left',
                  width: `var(--col-${cell.column.id}-size)`,
                }}
              >
                <div className="grid">
                  {loading ? (
                    <Skeleton loading />
                  ) : (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  )}
                </div>
              </TableCell>
            ))}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={colspan} className="h-24 text-center">
            No Results
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}

const getCommonPinningStyles = (column: Column<unknown>): CSSProperties => {
  const isPinned = column.getIsPinned()
  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `0px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 50 : 0,
  }
}
